import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiFilter, FiUser, FiMapPin, FiCalendar, FiBookOpen, FiAward, FiHome, FiX, FiMail } from 'react-icons/fi';
import { MobileAlumniCard, MobileAlumniNav, MobileAlumniBottomNav, MobileAlumniSidebar } from './components/AlumniMobileComponents';
import { PATHS, getAlumniPath } from './utils/platformPaths';

const AlumniDirectory = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    graduation_year: '',
    major: '',
    degree_type: '',
    current_employer: '',
    location: ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    performSearch();
  }, [searchQuery, filters, page]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
        const response = await fetch(`${backendUrl}/api/alumni/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setUserProfile(data);
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    };

    fetchUserProfile();
  }, []);

  const performSearch = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate(PATHS.login);
        return;
      }

      const params = new URLSearchParams();
      if (searchQuery) params.append('q', searchQuery);
      if (filters.graduation_year) params.append('graduation_year', filters.graduation_year);
      if (filters.major) params.append('major', filters.major);
      if (filters.degree_type) params.append('degree_type', filters.degree_type);
      if (filters.current_employer) params.append('current_employer', filters.current_employer);
      if (filters.location) params.append('location', filters.location);
      params.append('page', page);
      params.append('limit', '20');

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/api/alumni/directory/search?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          navigate(PATHS.login);
          return;
        }
        const errorText = await response.text();
        console.error('API Error:', response.status, errorText);
        throw new Error(`Failed to search alumni: ${response.status}`);
      }

      const data = await response.json();
      setSearchResults(data.alumni);
      setTotalResults(data.total);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      graduation_year: '',
      major: '',
      degree_type: '',
      current_employer: '',
      location: ''
    });
    setPage(1);
  };

  const viewProfile = (alumniId) => {
    const profilePath = getAlumniPath(`/alumni/profile/${alumniId}`);
    window.open(profilePath, '_blank');
  };

  return (
    <>
      {/* Mobile Navigation */}
      <MobileAlumniNav 
        title="Alumni Directory" 
        onMenuClick={() => setShowMobileSidebar(true)}
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-20 md:pb-8">
      {/* Header - Desktop */}
      <div className="hidden md:block mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Alumni Directory</h1>
            <p className="text-gray-600">Connect with fellow alumni from your university network.</p>
          </div>
          <button
            onClick={() => navigate(PATHS.dashboard)}
            className="inline-flex items-center px-4 py-2 border border-orange-300 rounded-md text-sm font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 transition-colors"
          >
            <FiHome className="h-4 w-4 mr-2" />
            Dashboard
          </button>
        </div>
      </div>

      {/* Header - Mobile */}
      <div className="md:hidden mb-6">
        <div className="text-center mb-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Alumni Directory</h1>
          <p className="text-gray-600">Connect with fellow alumni from your university network.</p>
        </div>
        <div className="flex justify-center">
          <button
            onClick={() => navigate(PATHS.dashboard)}
            className="inline-flex items-center px-4 py-2 border border-orange-300 rounded-md text-sm font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 transition-colors"
          >
            <FiHome className="h-4 w-4 mr-2" />
            Dashboard
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative max-w-2xl">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FiSearch className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Search by name or company..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Filter Toggle */}
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <FiFilter className="h-4 w-4 mr-2" />
          Filters
        </button>
        
        <div className="text-sm text-gray-500">
          {totalResults > 0 && `${totalResults} results found`}
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="mb-6 bg-gray-50 rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Graduation Year</label>
              <select
                value={filters.graduation_year}
                onChange={(e) => handleFilterChange('graduation_year', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">All Years</option>
                {Array.from({ length: 30 }, (_, i) => new Date().getFullYear() - i).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Major</label>
              <input
                type="text"
                placeholder="e.g. Computer Science"
                value={filters.major}
                onChange={(e) => handleFilterChange('major', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Degree</label>
              <select
                value={filters.degree_type}
                onChange={(e) => handleFilterChange('degree_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">All Degrees</option>
                <option value="Bachelor's">Bachelor's</option>
                <option value="Master's">Master's</option>
                <option value="PhD">PhD</option>
                <option value="Associate">Associate</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
              <input
                type="text"
                placeholder="Current employer"
                value={filters.current_employer}
                onChange={(e) => handleFilterChange('current_employer', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            
            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Results Grid */}
      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {searchResults.map((alumni) => (
            <AlumniProfileCard key={alumni.id} alumni={alumni} onViewProfile={viewProfile} />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && searchResults.length === 0 && (
        <div className="text-center py-12">
          <FiUser className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No alumni found</h3>
          <p className="mt-1 text-sm text-gray-500">
            Try adjusting your search terms or filters.
          </p>
        </div>
      )}

      {/* Pagination */}
      {totalResults > 20 && (
        <div className="mt-8 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {((page - 1) * 20) + 1} to {Math.min(page * 20, totalResults)} of {totalResults} results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={page * 20 >= totalResults}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileAlumniBottomNav 
        activeSection="directory"
        onNavigate={(path) => navigate(path)}
      />

      {/* Mobile Sidebar */}
      <MobileAlumniSidebar 
        isOpen={showMobileSidebar}
        onClose={() => setShowMobileSidebar(false)}
        userProfile={userProfile}
        onNavigate={(path) => navigate(path)}
      />
    </>
  );
};

const AlumniProfileCard = ({ alumni, onViewProfile }) => {
  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 hover:shadow-lg transition-shadow duration-200">
      <div className="p-6">
        {/* Profile Header */}
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            {alumni.headshot_url ? (
              <img
                className="h-16 w-16 rounded-full object-cover"
                src={alumni.headshot_url}
                alt={`${alumni.first_name} ${alumni.last_name}`}
              />
            ) : (
              <div className="h-16 w-16 rounded-full bg-gray-300 flex items-center justify-center">
                <FiUser className="h-8 w-8 text-gray-600" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              {alumni.first_name} {alumni.last_name}
            </h3>
            {alumni.show_current_position && alumni.current_position && (
              <p className="text-sm text-gray-600 truncate">{alumni.current_position}</p>
            )}
            {alumni.show_current_employer && alumni.current_employer && (
              <div className="flex items-center">
                {alumni.current_company_logo && (
                  <img
                    src={alumni.current_company_logo}
                    alt={alumni.current_employer}
                    className="h-4 w-4 rounded mr-1 bg-white"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
                <p className="text-sm text-blue-600 font-medium truncate">{alumni.current_employer}</p>
              </div>
            )}
          </div>
        </div>

        {/* Alumni Details */}
        <div className="mt-4 space-y-2">
          {alumni.show_graduation_year && alumni.graduation_year && (
            <div className="flex items-center text-sm text-gray-600">
              <FiCalendar className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>Class of {alumni.graduation_year}</span>
            </div>
          )}
          
          {alumni.show_major && alumni.major && (
            <div className="flex items-center text-sm text-gray-600">
              <FiBookOpen className="h-4 w-4 mr-2 flex-shrink-0" />
              <span className="truncate">{alumni.major}</span>
            </div>
          )}
          
          {alumni.show_degree_type && alumni.degree_type && (
            <div className="flex items-center text-sm text-gray-600">
              <FiAward className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>{alumni.degree_type}</span>
            </div>
          )}
          
          {alumni.show_university && alumni.university && (
            <div className="flex items-center text-sm text-gray-600">
              <FiMapPin className="h-4 w-4 mr-2 flex-shrink-0" />
              <span className="truncate">{alumni.university}</span>
            </div>
          )}
          
          {alumni.show_location && alumni.location && (
            <div className="flex items-center text-sm text-gray-600">
              <FiMapPin className="h-4 w-4 mr-2 flex-shrink-0" />
              <span className="truncate">{alumni.location}</span>
            </div>
          )}
        </div>

        {/* About Section */}
        {alumni.show_about && alumni.about && (
          <div className="mt-4">
            <p className="text-sm text-gray-700 line-clamp-3">
              {alumni.about.length > 120 ? `${alumni.about.substring(0, 120)}...` : alumni.about}
            </p>
          </div>
        )}

        {/* Action Button */}
        <div className="mt-6">
          <button
            onClick={() => onViewProfile(alumni.id)}
            className="w-full bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
          >
            View Profile
          </button>
        </div>
      </div>
    </div>
  );
};

export default AlumniDirectory;